/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import DS from 'ember-data';
import moment from 'moment-timezone';
import { isEmpty } from '@ember/utils';
import { storageFor } from 'ember-local-storage';
import { Periods, Entities } from 'cio/utils/metrics/common';
import { LAST_30_DAYS_RANGE_PRESET } from 'cio/utils/workflow-metrics';
import EnvironmentModel from 'cio/models/environment';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import actionInfo, { actionTypeName } from 'cio/utils/action-info';
import { ActionType } from 'cio/models/action';
import MetricsService from 'cio/services/metrics';
import { UserSettings } from 'cio/models/user';
import AnalyticsService from 'cio/services/analytics';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import AccountService from 'cio/services/account';
import { Grouping } from 'cio/utils/metrics/resolution';
import { deserialize as deserializeMetricResponse } from 'cio/utils/metrics/deserialize/metric';
import { Metric } from 'cio/utils/metrics/models/metric';
import { TimeRange } from 'cio/utils/time';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';

interface DashboardPageArgs {
  environmentModel: EnvironmentModel;
  setTransitionMetaData: (data: Record<string, any>) => unknown;

  startTime: string;
  endTime: string;
  onTimeRangeChange: (start: string, end: string) => unknown;

  resolution: Periods;
  onResolutionChange: (resolution: Periods) => unknown;

  grouping: Grouping;
  onGroupingChange: (grouping: Grouping) => unknown;
}

type ChannelCard = 'performance' | 'delivery';
const CARDS_WITH_CHANNELS: ChannelCard[] = ['performance', 'delivery'];

export default class DashboardPage extends Component<DashboardPageArgs> {
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service account!: AccountService;
  @service environment!: EnvironmentService;
  @service('metrics') metricsService!: MetricsService;
  @service store!: DS.Store;
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;

  @storageFor('cio-alerts') localStorage: any;

  @tracked environmentId: string | null = null;

  @tracked timeRange!: TimeRange;
  @tracked trackingResolution: Periods | 'week' = Periods.DAYS;

  @tracked selectedSegments: (string | null)[] = [null, null];
  @tracked selectedChannels: { performance: string; delivery: string } = {
    performance: 'email',
    delivery: 'email',
  };

  @tracked deliveryMetrics: Metric[] = [];

  channels = [
    'email_action',
    'twilio_action',
    'push_action',
    'webhook_action',
    'slack_action',
    'urban_airship_action',
    'in_app_action',
  ]
    .filter((type: ActionType) => {
      return this.environment.enabledAction(type);
    })
    .map((type: ActionType) => {
      let info = actionInfo(type);

      return {
        label: info.name,
        value: actionTypeName(type),
        icon: info.icon,
      };
    });

  constructor(owner: unknown, args: DashboardPageArgs) {
    super(owner, args);

    let timeRange!: TimeRange;

    if (!isEmpty(this.args.startTime) && !isEmpty(this.args.endTime)) {
      timeRange = {
        start: moment(this.args.startTime),
        end: moment(this.args.endTime),
      };
    } else {
      timeRange = LAST_30_DAYS_RANGE_PRESET.getRange();
    }

    this.timeRange = timeRange;
  }

  get groupings() {
    let isMonthlyTimePeriod = this.args.resolution === Periods.MONTHS;

    let groupings = [{ label: 'Daily', value: Grouping.Daily, disabled: isMonthlyTimePeriod }];

    let { timeRange } = this;

    let daysInRange = (timeRange.end?.diff(timeRange.start, 'days') ?? 0) + 1;

    if (daysInRange >= 7) {
      groupings.push({ label: 'Weekly', value: Grouping.Weekly, disabled: isMonthlyTimePeriod });
    }

    if (daysInRange >= 62) {
      groupings.push({ label: 'Monthly', value: Grouping.Monthly, disabled: false });
    }

    return groupings;
  }

  get performanceMeasures() {
    if (!isDeliveryMeasureEnabledForChannel('opened', 'email')) {
      return [
        { name: 'clicked', isRate: true },
        { name: 'converted', isRate: true },
        { name: 'unsubscribed', isRate: true },
        { name: 'spammed', isRate: true },
        { name: 'opened', isRate: true },
      ];
    }

    return [
      { name: 'opened', isRate: true },
      { name: 'clicked', isRate: true },
      { name: 'converted', isRate: true },
      { name: 'unsubscribed', isRate: true },
      { name: 'spammed', isRate: true },
    ];
  }

  @action
  onInsert() {
    this.resetInitialStateAndRunTasks();
  }

  @action
  resetInitialStateAndRunTasks() {
    this.environmentId = this.args.environmentModel.id;

    // Reset previous states, because the environment may have changed, so the
    // old segment IDs have no use anymore
    this.fetchDashboardSettings.cancelAll({ resetState: true });
    this.fetchEnvironmentDeliveryMetrics.cancelAll({ resetState: true });

    this.fetchDashboardSettings.perform();
    this.fetchEnvironmentDeliveryMetrics.perform();
  }

  @action
  onUpdate() {
    if (this.environmentId !== this.args.environmentModel.id) {
      this.setDefaults();

      this.resetInitialStateAndRunTasks();
    }
  }

  fetchEnvironmentDeliveryMetrics = task({ drop: true }, async () => {
    let range = {
      start: moment.tz(`${this.timeRange.start?.format('YYYY-MM-DD')} 00:00:00`, 'Etc/GMT+4'),
      end: moment.tz(`${this.timeRange.end?.format('YYYY-MM-DD')} 00:00:00`, 'Etc/GMT+4'),
    };

    let result = await this.metricsService.fetchEnvironmentDeliveryMetrics(
      this.args.environmentModel.id,
      this.args.resolution === Periods.MONTHS ? Periods.MONTHS : Periods.CUSTOM,
      range,
    );

    this.deliveryMetrics = (result?.metrics ?? []).map((data: any) =>
      deserializeMetricResponse(
        { metric: data },
        Entities.ENVIRONMENT,
        this.args.environmentModel.id,
        Periods.CUSTOM,
        this.args.grouping,
        this.args.resolution === Periods.MONTHS ? Periods.MONTHS : Periods.DAYS,
        range,
        range,
        `${data.type}_action`,
        { version: 2 },
      ),
    );
  });

  fetchDashboardSettings = task({ restartable: true }, async () => {
    let result = (await this.auth.user?.getUserSettings()) ?? {};

    let segmentIds: string[] = result.segment_ids ?? [];

    if (segmentIds.length > 0) {
      this.setSelectedSegments(segmentIds);
    }

    CARDS_WITH_CHANNELS.forEach((card) => {
      let resultChannels = result[card]?.channels ?? [];
      // The backend accepts an array for channels, but the UI only allows one
      // channel selection, so we take the first array item if available.
      let channel = isEmpty(resultChannels) ? 'email' : resultChannels[0];
      this.setSelectedChannel(card, channel);
    });
  });

  updateDashboardSettings = task({ restartable: true }, async (settings: Partial<UserSettings>) => {
    if (this.auth.user == null) {
      this.alerts.error(
        'dashboard-page.update-dashboard-settings.user-missing',
        'Unable to find current user. Refresh the page and try again.',
      );
      return;
    }

    return await this.auth.user?.updateUserSettings(settings);
  });

  updateDashboardSegmentSettings = task({ restartable: true }, async (segmentIds: string[]) => {
    await this.updateDashboardSettings.perform({ segment_ids: segmentIds });

    this.setSelectedSegments(segmentIds);
  });

  updateDashboardChannelSettings = task({ restartable: true }, async (card: ChannelCard, channel: string) => {
    let data: Partial<UserSettings> = {
      [card]: { channels: [channel] },
    };

    this.setSelectedChannel(card, channel);

    await this.updateDashboardSettings.perform(data);
  });

  @action
  setSelectedSegments(ids: string[]) {
    let _ids = [...ids];

    let selectedSegments = _ids.map((id) => (id === '0' ? null : id));

    // We show two segments in the dashboard, and if only one item is set in the
    // DashboardSettings, we need to add an empty item to the segments list
    if (selectedSegments.length === 1) {
      selectedSegments.push(null);
    }

    this.selectedSegments = selectedSegments;
  }

  @action
  setSelectedChannel(card: ChannelCard, channel: string) {
    this.selectedChannels = {
      ...this.selectedChannels,
      [card]: channel,
    };
  }

  @action
  setDefaults() {
    this.timeRange = LAST_30_DAYS_RANGE_PRESET.getRange();
    this.args.onResolutionChange(Periods.DAYS);
    this.trackingResolution = Periods.DAYS;
    this.selectedSegments = [null, null];
  }

  @action
  onRangeSelect(timeRange: TimeRange, trackingResolution: Periods | 'week') {
    this.timeRange = timeRange;
    this.trackingResolution = trackingResolution;

    // The API doesn't actually recognize "week" as a period. We only use it
    // in the UI as a preset, and need it to be not named "custom" for
    // analytics tracking purposes. In reality the preset works just like any
    // other custom preset.
    let resolution = trackingResolution === 'week' ? Periods.CUSTOM : trackingResolution;

    this.args.onResolutionChange(resolution);

    if (resolution === Periods.MONTHS) {
      this.onGroupingChange(Grouping.Monthly);
    } else if (!this.groupings.some((grouping) => grouping.value === this.args.grouping)) {
      // If the current time grouping doesn't exist as an option anymore (for
      // example, after rerunning the report with a different range), select the
      // default daily grouping again
      this.onGroupingChange(Grouping.Daily);
    }

    if (timeRange?.start != null && timeRange?.end != null) {
      this.args.onTimeRangeChange(timeRange.start.format('YYYY-MM-DD'), timeRange.end.format('YYYY-MM-DD'));
    }

    this.fetchEnvironmentDeliveryMetrics.perform();

    this.trackTimeframeFiltersChanged('timeframe');
  }

  @action
  onGroupingChange(grouping: Grouping) {
    this.args.onGroupingChange(grouping);

    this.fetchEnvironmentDeliveryMetrics.perform();

    this.trackTimeframeFiltersChanged('groupBy');
  }

  @action
  trackNavigateAway(data: Record<string, any>) {
    this.args.setTransitionMetaData(data);
  }

  @action
  trackChartChanged(data: Record<string, any>) {
    this.analytics.track('dashboard', 'chart_changed', data);
  }

  @action
  trackChartHover(data: Record<string, any>) {
    this.analytics.track('dashboard', 'chart_hover', data);
  }

  @action
  trackTimeframeFiltersChanged(editType: string) {
    this.analytics.track('dashboard', 'timeframe_filters_changed', {
      timeRange: this.timeRange,
      timePeriod: this.trackingResolution,
      groupBy: this.args.grouping,
      editType,
    });
  }
}
